import React, { useState, useEffect } from "react";
import { Button } from "reactstrap";
import { getLevel } from "../../constants/items";
import StatsModal from "../../components/Common/StatsModal";
import "../Items/ManageItem.css";
import api from "../../api";

const BoxWidget = () => {
  const [pokemon, setPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [isSearching, setIsSearching] = useState(false);
  
  // stats modal state
  const [statsModal, setStatsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const toggleStatsModal = () => setStatsModal(on => !on);

  const fetchPokemon = React.useCallback((page = 1, limit = 12) => {
    setLoading(true);
    
    api
      .get("/v1/pokemon", {
        params: { page, limit },
        withCredentials: true
      })
      .then((response) => {
        const allPokemon = response.data.data || response.data.pokemon || [];
        setPokemon(allPokemon);
        setTotalRows(allPokemon.length);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching box:", err);
        setLoading(false);
      });
  }, []);

  const searchPokemon = React.useCallback((query) => {
    if (!query.trim()) return;
    
    setLoading(true);
    api
      .get("/v1/pokemon/search", {
        params: { query },
        withCredentials: true
      })
      .then((response) => {
        const searchResults = response.data.data || response.data.pokemon || [];
        setPokemon(searchResults);
        setTotalRows(searchResults.length);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error searching Pokemon:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchPokemon(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage, fetchPokemon]);

  const openStats = async (mon) => {
    setModalLoading(true);
    setStatsModal(true);
    try {
      const { data } = await api.get(`/v1/pokemon/${mon.id}`, { withCredentials: true });
      setModalData(data);
    } catch (err) {
      setModalData({ error: err.response?.data?.detail || "Failed to load" });
    } finally {
      setModalLoading(false);
    }
  };

  // Calculate pagination info
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage + 1;
  const endIndex = Math.min(totalRows, currentPage * rowsPerPage);
  
  // For search mode, paginate client-side
  const displayedList = isSearching
    ? pokemon.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : pokemon;

  return (
    <div className="mb-4">
      <h5 style={{ 
        color: "#ffffff", 
        fontWeight: "600",
        marginBottom: "1rem",
        paddingLeft: "0.5rem",
        borderLeft: "3px solid rgba(59, 130, 246, 0.8)"
      }}>Box</h5>
      
      {/* Search Bar */}
      <div style={{ marginBottom: '1rem', display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <input
            type="text"
            placeholder="Search Pokémon..."
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              if (value.trim() === "") {
                setIsSearching(false);
                fetchPokemon(1, rowsPerPage);
              } else {
                setIsSearching(true);
                searchPokemon(value);
              }
            }}
            style={{
              width: '100%',
              padding: '8px 35px 8px 12px',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              color: '#ffffff',
              fontSize: '0.9rem'
            }}
          />
          <div style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'rgba(255, 255, 255, 0.5)'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="21 21l-4.35-4.35"></path>
            </svg>
          </div>
        </div>
        
        <select
          value={rowsPerPage}
          onChange={(e) => {
            const newLimit = parseInt(e.target.value, 10);
            setRowsPerPage(newLimit);
            setCurrentPage(1);
            if (!isSearching) fetchPokemon(1, newLimit);
          }}
          style={{
            padding: '8px',
            borderRadius: '6px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            color: '#ffffff',
            fontSize: '0.9rem'
          }}
        >
          <option value={12}>12</option>
          <option value={20}>20</option>
          <option value={40}>40</option>
        </select>
      </div>

      {/* Pokemon Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: '12px',
        marginBottom: '1rem'
      }}>
        {loading ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', padding: '2rem' }}>
            <p>Loading…</p>
          </div>
        ) : displayedList.length > 0 ? (
          displayedList.map((mon) => {
            const level = getLevel(mon.totalexp);
            return (
              <div
                key={mon.id}
                onClick={() => openStats(mon)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '12px',
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <img
                  src={`/icon/${mon.name}.png`}
                  alt={mon.nickname || mon.name}
                  style={{ width: '48px', height: '48px', marginBottom: '8px' }}
                />
                <div style={{ 
                  color: '#ffffff', 
                  fontSize: '0.8rem', 
                  fontWeight: '500',
                  marginBottom: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {mon.nickname || mon.name}
                </div>
                <div style={{
                  background: '#3b82f6',
                  color: '#fff',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  Lvl {level.toLocaleString()}
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)', padding: '2rem' }}>
            <p>No Pokémon found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
            Showing {startIndex}–{endIndex} of {totalRows}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button color="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)}>
              Prev
            </Button>
            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8rem' }}>
              {currentPage} / {totalPages}
            </span>
            <Button color="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <StatsModal
        isOpen={statsModal}
        toggle={toggleStatsModal}
        loading={modalLoading}
        data={modalData}
      />
    </div>
  );
};

export default BoxWidget;
